import { type ReactNode } from 'react'
import { useAuthActions } from '@convex-dev/auth/react'
import { Authenticated, Unauthenticated, AuthLoading } from 'convex/react'
import { Button } from '@/components/ui/button'

/**
 * The door.
 *
 * Google establishes who you are; the invite list on the server decides
 * whether you may be here. Both checks happen server-side — the Convex
 * deployment URL ships inside this bundle, so a gate that only hid the UI
 * would leave the data readable by anyone who opened the page and looked.
 *
 * There is no password field, no signup form and no "forgot" link, because
 * there is nothing here to forget. That is the main thing the account change
 * bought: no hashes, no reset mail, and nothing to leak.
 */
export function SignInGate({ children }: { children: ReactNode }) {
  return (
    <>
      <AuthLoading>
        <div className="flex min-h-dvh items-center justify-center">
          <p className="text-muted-foreground text-sm">Loading…</p>
        </div>
      </AuthLoading>
      <Unauthenticated>
        <SignInScreen />
      </Unauthenticated>
      <Authenticated>{children}</Authenticated>
    </>
  )
}

function SignInScreen() {
  const { signIn } = useAuthActions()

  return (
    <main className="flex min-h-dvh flex-col items-center justify-center gap-6 px-6">
      <div className="w-full max-w-xs space-y-2 text-center">
        <h1 className="text-xl font-bold">TasksHabits</h1>
        <p className="text-muted-foreground text-sm">
          Sign in to pick up where you left off.
        </p>
      </div>

      <Button className="w-full max-w-xs" onClick={() => void signIn('google')}>
        Continue with Google
      </Button>

      {/*
       * Says plainly that not everyone can get in. Someone who is not on the
       * list would otherwise sign in with Google, watch it fail, and have no
       * way to tell a broken app from a closed door.
       */}
      <p className="text-muted-foreground max-w-xs text-center text-xs">
        This app is invite-only. If your account is not on the list, sign-in
        will not complete.
      </p>
    </main>
  )
}
